/**
 * Shipping and Returns page
 */

import { ContentBlock, Box } from 'braid-design-system';
import React from 'react';
import { Link } from 'react-router';

import { PageHead } from '../../components/PageHead/PageHead';
import {
  policySections,
  type PolicyTextSegment,
} from '../../data/policySections';

import * as styles from './Policies.css';

const renderSegments = (segments: PolicyTextSegment[]) =>
  segments.map((segment, index) => {
    if (!segment.href) {
      return <React.Fragment key={index}>{segment.text}</React.Fragment>;
    }

    if (segment.href.startsWith('/')) {
      return (
        <Link key={index} to={segment.href} className={styles.inlineLink}>
          {segment.text}
        </Link>
      );
    }

    return (
      <a key={index} href={segment.href} className={styles.inlineLink}>
        {segment.text}
      </a>
    );
  });

export const Policies: React.FC = () => (
  <ContentBlock width="large">
    <PageHead
      title="Shipping and Returns"
      description="Shipping, returns, STL licensing, and custom order details for Fae Forest."
      path="/shipping-and-returns"
    />
    <Box paddingY="large">
      <article className={styles.container}>
        <h1 className={styles.heading}>Shipping and Returns</h1>
        <nav className={styles.toc} aria-label="Shipping and returns sections">
          {policySections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={styles.tocLink}
            >
              {section.title}
            </a>
          ))}
        </nav>
        {policySections.map((section) => (
          <section key={section.id} id={section.id} className={styles.section}>
            <h2 className={styles.sectionTitle}>{section.title}</h2>
            {section.subsections.map((subsection) => (
              <div key={subsection.heading} className={styles.subsection}>
                <h3 className={styles.subsectionTitle}>{subsection.heading}</h3>
                {subsection.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className={styles.body}>
                    {paragraph}
                  </p>
                ))}
                {subsection.richParagraphs?.map((segments) => (
                  <p
                    key={segments.map((s) => s.text).join('')}
                    className={styles.body}
                  >
                    {renderSegments(segments)}
                  </p>
                ))}
                {subsection.bullets && (
                  <ul className={styles.list}>
                    {subsection.bullets.map((item) => (
                      <li key={item} className={styles.listItem}>
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        ))}
      </article>
    </Box>
  </ContentBlock>
);
